import { RefreshCw } from 'lucide-react';

import { Card } from '@@/primitives/Card';
import { Badge } from '@@/Badge';

import { ReconcileBadges } from '../../components/ReconcileBadges';
import { WorkflowArtifact } from '../types';

/**
 * The policy each reconciled artifact is kept under.
 *
 * These decide what the engine does to a cluster without anybody asking it to,
 * so they belong beside the workflow rather than only inside the form that sets
 * them. Reading them used to mean opening Edit, which is the one place changing
 * them by accident is possible.
 */
export function ReconcileSection({
  artifacts,
}: {
  artifacts: Array<WorkflowArtifact>;
}) {
  const reconciled = artifacts.filter(
    (artifact) => artifact.type === 'gitopsDeployment' && artifact.reconcile
  );

  if (!reconciled.length) {
    return null;
  }

  const interval = reconciled[0].reconcile?.interval;

  return (
    <Card.Container aria-label="Reconciliation">
      <Card.Header
        icon={RefreshCw}
        title="Reconciliation"
        // The interval belongs to the workflow rather than to any one
        // artifact, which is why it sits in the header and not in a row.
        actions={interval ? <Badge type="muted">Every {interval}</Badge> : null}
      />
      <div className="divide-y divide-solid divide-gray-3 th-dark:divide-gray-9">
        {reconciled.map((artifact) => (
          <ReconcileRow key={artifact.id} artifact={artifact} />
        ))}
      </div>
    </Card.Container>
  );
}

function ReconcileRow({ artifact }: { artifact: WorkflowArtifact }) {
  return (
    <div
      className="flex flex-col gap-2 px-4 py-3"
      data-cy={`workflow-reconcile-row-${artifact.id}`}
    >
      <p className="m-0 text-xs font-semibold tracking-wide text-gray-6 uppercase th-dark:text-gray-6">
        {artifact.name}
      </p>
      <ReconcileBadges artifact={artifact} />
    </div>
  );
}
